import React, { useState } from 'react';
import { Form, Input, Button, Card, Tabs, message, Typography } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { signInWithEmail, signUpWithEmail, signInAnonymously } from '@/services/supabase';

const { Title } = Typography;

interface AuthFormProps {
  onLoginSuccess: () => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const { error } = await signInWithEmail(values.email, values.password);
      if (error) {
        message.error(error.message || '登录失败');
        return;
      }
      message.success('登录成功！');
      onLoginSuccess();
    } catch (err) {
      message.error('登录时发生错误');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: { email: string; password: string; confirmPassword: string }) => {
    if (values.password !== values.confirmPassword) {
      message.error('两次输入的密码不一致');
      return;
    }

    setLoading(true);
    try {
      const { error } = await signUpWithEmail(values.email, values.password);
      if (error) {
        message.error(error.message || '注册失败');
        return;
      }
      message.success('注册成功！请检查邮箱验证链接（如需要）');
      // 注册成功后切换到登录页
      setActiveTab('login');
    } catch (err) {
      message.error('注册时发生错误');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnonymousLogin = async () => {
    setLoading(true);
    try {
      const { userId, error } = await signInAnonymously();
      if (error || !userId) {
        message.error('匿名登录失败');
        return;
      }
      message.success('已使用匿名模式登录');
      onLoginSuccess();
    } catch (err) {
      message.error('登录时发生错误');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loginItems = [
    {
      key: 'login',
      label: '登录',
      children: (
        <Form onFinish={handleLogin} layout="vertical">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="邮箱" size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="密码" size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              登录
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'register',
      label: '注册',
      children: (
        <Form onFinish={handleRegister} layout="vertical">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="邮箱" size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="密码" size="large" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            rules={[{ required: true, message: '请确认密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="确认密码" size="large" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              注册
            </Button>
          </Form.Item>
        </Form>
      )
    }
  ];

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '100vh',
      background: '#f0f2f5'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0 }}>
            <UserOutlined style={{ marginRight: 8 }} />
            健康监测
          </Title>
          <p style={{ color: '#666', margin: '8px 0 0' }}>登录以同步您的健康数据</p>
        </div>

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab} 
          items={loginItems}
          centered
        />

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button type="link" onClick={handleAnonymousLogin} disabled={loading}>
            暂不登录，使用离线模式
          </Button>
        </div>

        <div style={{ marginTop: 16, padding: '12px', background: '#f6ffed', borderRadius: 4 }}>
          <p style={{ margin: 0, fontSize: 12, color: '#52c41a' }}>
            💡 提示：使用邮箱登录后，您的数据将在所有设备间自动同步
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AuthForm;
